import React, { useCallback, useState } from "react";
import { LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStudentAuth } from "../hooks/useStudentAuth";

export interface LogoutButtonProps {
  /** Estilo compacto: solo icono */
  compact?: boolean;
  /** Variante para fondos oscuros (learn canvas) */
  variant?: "dark" | "light" | "serenity";
  tone?: "dark" | "light";
  className?: string;
}

export const LogoutButton: React.FC<LogoutButtonProps> = ({
  compact = true,
  variant = "dark",
  tone = "dark",
  className = "",
}) => {
  const navigate = useNavigate();
  const { user, logout } = useStudentAuth();
  const [pending, setPending] = useState(false);

  const handleLogout = useCallback(async () => {
    if (pending) return;
    setPending(true);
    try {
      await logout();
      navigate("/", { replace: true });
    } finally {
      setPending(false);
    }
  }, [logout, navigate, pending]);

  if (!user) return null;

  if (variant === "serenity") {
    const linkClass =
      tone === "light"
        ? "text-stone-500 hover:text-stone-800"
        : "text-white/40 hover:text-white/80";
    return (
      <button
        type="button"
        onClick={handleLogout}
        disabled={pending}
        className={`text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-50 ${linkClass} ${className}`}
        title="Cerrar sesión"
      >
        {pending ? "…" : "Salir"}
      </button>
    );
  }

  const isDark = variant === "dark";
  const initial =
    user.displayName?.charAt(0)?.toUpperCase() ||
    user.email?.charAt(0)?.toUpperCase() ||
    "?";

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
        style={{
          backgroundColor: isDark ? "rgba(201,64,90,0.85)" : "#c9405a",
        }}
        title={user.displayName || user.email || "Sesión activa"}
      >
        {initial}
      </span>
      <button
        type="button"
        onClick={handleLogout}
        disabled={pending}
        className={`flex items-center gap-1.5 rounded-full transition-colors disabled:opacity-50 ${
          compact ? "h-7 w-7 justify-center" : "h-7 px-2.5"
        }`}
        style={{
          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
          color: isDark ? "rgba(248,250,252,0.88)" : "#8A8078",
        }}
        title="Cerrar sesión"
      >
        <LogOut className="h-3.5 w-3.5" />
        {!compact && (
          <span className="text-[11px] font-semibold pr-0.5">
            {pending ? "…" : "Salir"}
          </span>
        )}
      </button>
    </div>
  );
};
